/**
 * The model registry: `policy.json` validated at import time, plus
 * `resolveModel(task)` which applies env overrides and the thinking mapping.
 *
 * This entry point deliberately imports nothing from the AI SDK, so packages
 * that only need a model id or the embedding contract (`@graftus/schema`,
 * which the web and mobile apps bundle) stay light. The AI SDK helpers live in
 * `./sdk`.
 *
 * `policy.json` is imported, not read from disk, so tsup bundles it into the
 * Firebase functions. The Python side (`tools/chat-evals`, `apps/agent-brain`)
 * reads the same file with its own loader.
 */
import { z } from "zod";
import policyJson from "../policy.json";
import { thinkingConfigFor, type ThinkingSetting } from "./thinking";

const TaskPolicySchema = z
  .object({
    /**
     * `provider:model-id`, the convention LangChain's `init_chat_model` reads
     * unchanged (so the Gemini API is `google_genai`, not `google`).
     */
    model: z.string().regex(/^[a-z][a-z0-9_-]*:[^\s:]+$/, "model must be provider:model-id"),
    /** Env var whose (non-empty) value replaces `model`; bare ids keep the policy's provider. */
    env: z.string().regex(/^[A-Z][A-Z0-9_]*$/).optional(),
    temperature: z.number().min(0).max(2).optional(),
    thinking: z.enum(["off", "low", "medium", "default"]).optional(),
    /** Env var with an explicit thinking budget (tokens) that overrides `thinking`. */
    thinkingBudgetEnv: z.string().regex(/^[A-Z][A-Z0-9_]*$/).optional(),
    /** Embedding tasks only: the stored vector width. */
    dimensions: z.number().int().positive().optional(),
    /** Reserved. Stays empty until fallbacks are designed (ARCHITECTURE.md, "Later"). */
    fallbacks: z.array(z.string()).max(0).default([]),
  })
  .strict();

const PolicySchema = z
  .object({
    $comment: z.string().optional(),
    version: z.literal(1),
    tasks: z.record(z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/), TaskPolicySchema),
  })
  .strict();

export type TaskPolicy = z.infer<typeof TaskPolicySchema>;

/** Every task name in `policy.json`; an unknown name is a type error. */
export type TaskName = keyof typeof policyJson.tasks;

export const MODEL_POLICY = PolicySchema.parse(policyJson) as {
  version: 1;
  tasks: Record<TaskName, TaskPolicy>;
};

export const TASK_NAMES = Object.keys(MODEL_POLICY.tasks) as TaskName[];

type JsonValue = null | string | number | boolean | JsonValue[] | { [key: string]: JsonValue };

/**
 * Registry provider names are LangChain's (`init_chat_model`); the AI SDK
 * side keys `providerOptions` by its own provider name. One table, used by
 * `resolveModel` here and by `./sdk` to pick the provider factory.
 */
export const AI_SDK_PROVIDERS: Record<string, { providerOptionsKey: string }> = {
  google_genai: { providerOptionsKey: "google" },
};

/** Shape the AI SDK accepts as `providerOptions`. */
export type ModelProviderOptions = Record<string, Record<string, JsonValue>>;

export interface ResolvedModel {
  task: TaskName;
  /** Registry (LangChain) provider name, `google_genai` today. */
  provider: string;
  /** Bare id as the provider API expects it, e.g. `gemini-2.5-flash`. */
  modelId: string;
  /** `provider:model-id`, after any env override. */
  model: string;
  /** Spread into the call's `providerOptions`; `{}` when nothing applies. */
  providerOptions: ModelProviderOptions;
  settings: { temperature?: number };
  /** Embedding tasks: the vector width to request and store. */
  dimensions?: number;
  fallbacks: string[];
  /** `"policy"`, or the env var the id came from. */
  source: "policy" | string;
}

function splitModel(model: string, defaultProvider?: string): { provider: string; modelId: string } {
  const at = model.indexOf(":");
  if (at === -1) {
    if (!defaultProvider) throw new Error(`model "${model}" is missing its provider prefix`);
    return { provider: defaultProvider, modelId: model };
  }
  return { provider: model.slice(0, at), modelId: model.slice(at + 1) };
}

/**
 * `process.env` where there is one. `@graftus/schema` re-exports the embedding
 * contract from here and is bundled into the web and mobile apps, where
 * `process` does not exist; no task those bundles touch has an env override.
 */
function defaultEnv(): Record<string, string | undefined> {
  return typeof process !== "undefined" && process.env ? process.env : {};
}

/**
 * Resolve the model a task runs on: the policy entry, overridden by its env
 * var when set (e.g. `CHAT_MODEL_ID=gemini-2.5-pro` or
 * `CHAT_MODEL_ID=google_genai:gemini-2.5-pro`), with the thinking setting mapped to
 * provider options for the resolved model family.
 *
 * `env` defaults to `process.env`; tests pass their own.
 */
export function resolveModel(
  task: TaskName,
  env: Record<string, string | undefined> = defaultEnv(),
): ResolvedModel {
  const entry = MODEL_POLICY.tasks[task];
  if (!entry) throw new Error(`unknown model task "${task}"`);

  const policyModel = splitModel(entry.model);
  const override = entry.env ? env[entry.env]?.trim() : undefined;
  const { provider, modelId } = override
    ? splitModel(override, policyModel.provider)
    : policyModel;

  const thinkingConfig = thinkingConfigFor(
    modelId,
    entry.thinking as ThinkingSetting | undefined,
    entry.thinkingBudgetEnv
      ? { raw: env[entry.thinkingBudgetEnv], envName: entry.thinkingBudgetEnv }
      : undefined,
  );
  const optionsKey = AI_SDK_PROVIDERS[provider]?.providerOptionsKey;
  const providerOptions: ModelProviderOptions =
    thinkingConfig && optionsKey ? { [optionsKey]: { thinkingConfig } } : {};

  return {
    task,
    provider,
    modelId,
    model: `${provider}:${modelId}`,
    providerOptions,
    settings: entry.temperature === undefined ? {} : { temperature: entry.temperature },
    ...(entry.dimensions === undefined ? {} : { dimensions: entry.dimensions }),
    fallbacks: entry.fallbacks,
    source: override && entry.env ? entry.env : "policy",
  };
}

/** Looks up the task's policy entry without resolving env overrides. */
export function taskPolicy(task: TaskName): TaskPolicy {
  return MODEL_POLICY.tasks[task];
}
